import React, { useState, useEffect } from "react";
import "./PublishPopup.css";
import TagsInput from "./TagsInput";
import AddPublication from "../../../../utils/database/AddPublication";
import GetDistinctCategories from "../../../../utils/database/GetDistinctCategories";
import GetDistinctSubcategories from "../../../../utils/database/GetDinctinctSubcategories";
import { useMsal } from "@azure/msal-react";
import UserSearchPicker from "../../../common/UserSearchPicker/UserSearchPicker";


import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Typography,
    CircularProgress,
    Box,
    FormControl,
    Autocomplete,
    TextareaAutosize,
    createFilterOptions
} from "@mui/material";

const filter = createFilterOptions();

const PublishPopup = ({ open, onPublish, onClose, chatObject }) => {
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);

    const [category, setCategory] = useState("");
    const [subCategory, setSubCategory] = useState("");
    const [description, setDescription] = useState("");
    const [tags, setTags] = useState([]);
    // Access control: empty array = visible to everyone. Non-empty = only author + listed users.
    const [allowedUsers, setAllowedUsers] = useState([]);

    const [isCreatingCategory, setIsCreatingCategory] = useState(false);
    const [newCategory, setNewCategory] = useState("");

    const [isCreatingSubCategory, setIsCreatingSubCategory] = useState(false);
    const [newSubCategory, setNewSubCategory] = useState("");

    const [errors, setErrors] = useState({});
    const [publishError, setPublishError] = useState("");

    const [inputValue, setInputValue] = React.useState('');
    const [categoryInputValue, setCategoryInputValue] = useState('');

    const [loader, setLoader] = useState(false);

    const { instance } = useMsal();
    const account = instance.getActiveAccount();

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const fetchedCategories = await GetDistinctCategories();
                setCategories(fetchedCategories);
            } catch (error) {
                // console.error("Error fetching categories:", error);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        setErrors("")
        setPublishError("")
    }, [tags, category, subCategory, description]);

    useEffect(() => {
        if (category && !isCreatingCategory) {
            const fetchSubCategories = async () => {
                try {
                    const fetchedSubCategories = await GetDistinctSubcategories(category);
                    setSubCategories(fetchedSubCategories);
                } catch (error) {
                    // console.error("Error fetching subcategories:", error);
                }
            };
            fetchSubCategories();
        } else {
            setSubCategories([]);
        }
    }, [category, isCreatingCategory]);

    const validateFields = () => {
        const newErrors = {};
        if (!category && !newCategory) {
            newErrors.category = "Please select or create a category.";
        }
        if (!subCategory && !newSubCategory) {
            newErrors.subCategory = "Please select or create a subcategory.";
        }
        if (!description) {
            newErrors.description = "Description is required.";
        }
        if (tags.length === 0) {
            newErrors.tags = "At least one tag is required.";
        }
        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handlePublishClick = async (e) => {
        e.preventDefault();
        if (validateFields() && account) {
            const userId = account.localAccountId || account.homeAccountId.split('.')[0];
            const selectedCategory = category || newCategory;
            const selectedSubCategory = subCategory || newSubCategory;
            setLoader(true);

            try {
                const result = await AddPublication(
                    chatObject.chatId,
                    userId,
                    selectedCategory,
                    selectedSubCategory,
                    description,
                    tags,
                    allowedUsers
                );

                if (result.success) {
                    onPublish();
                    setPublishError("");
                    onClose();
                } else {
                    setPublishError(result.error);
                }
            } catch (error) {
                // console.error("Error publishing:", error);
                setPublishError("An unexpected error occurred. Please try again.");
            } finally {
                setLoader(false);
            }
        } else {
            setPublishError("Please fill all required fields.");
        }
    };


    const handleCategoryChange = (_, newValue) => {
        if (typeof newValue === 'string') {
            // New category is entered as text
            setCategory(newValue);
            // Optionally, add the new category to the list if needed
            if (!subCategories.includes(newValue)) {
                setCategories([...categories, newValue]);
            }
        } else if (newValue && newValue.inputValue) {
            // Create a new category based on user input
            const newCategory = newValue.inputValue;
            setCategory(newCategory);
            setCategories([...categories, newCategory]);
        } else {
            // Predefined option selected
            setCategory(newValue);
        }
    };
    const handleSubCategoryChange = (_, newValue) => {
        if (typeof newValue === 'string') {
            // New category is entered as text
            setSubCategory(newValue);
            // Optionally, add the new category to the list if needed
            if (!subCategories.includes(newValue)) {
                setSubCategories([...subCategories, newValue]);
            }
        } else if (newValue && newValue.inputValue) {
            // Create a new category based on user input
            const newCategory = newValue.inputValue;
            setSubCategory(newCategory);
            setSubCategories([...subCategories, newCategory]);
        } else {
            // Predefined option selected
            setSubCategory(newValue);
        }
    };

    const handleDescriptionChange = (e) => {
        setErrors("")
        setErrors((prevErrors) => ({ ...prevErrors, description: "" }));
        setDescription(e.target.value);
    };



    const styles = {
        dialog: {
            '& .MuiDialog-paper': {
                width: '90vw',
                maxWidth: '200',
                border: '2px solid #333366',
                borderRadius: '10px',
            }
        },
        dialogTitle: {
            color: '#333366',
            textAlign: 'center',
            fontWeight: 'bold',
            '& .MuiTypography-root': {
                fontSize: '1.5rem',
                fontWeight: 'bolder'
            }
        },
        formControl: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            mb: 2
        },
        label: {
            color: '#333366',
            fontWeight: 'bold',
            mb: 0.5
        },
        input: {
            bgcolor: '#fff',
            '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                    borderColor: '#99cc33',
                },
                '&.Mui-focused fieldset': {
                    borderColor: '#99cc33',
                    borderWidth: '2px',
                },
            }
        },
        errorText: {
            color: 'error.main',
            fontSize: '0.75rem',
            mt: 0.5
        },
        button: {
            textTransform: 'none',
            px: 3,
            py: 1
        },
        publishButton: {
            bgcolor: '#99cc33',
            color: '#fff',
            '&:hover': {
                bgcolor: '#88bb22',
            }
        },
        closeButton: {
            bgcolor: '#ccc',
            color: '#333',
            '&:hover': {
                bgcolor: '#bbb',
            }
        },

    };


    return (
        <Dialog
            open={open}
            onClose={onClose}
            sx={styles.dialog}
        >
            <DialogTitle sx={styles.dialogTitle}>
                Publish Chat - {chatObject?.chatName}
            </DialogTitle>

            <DialogContent>
                <Box component="form" noValidate sx={{ mt: 1 }}>
                    <FormControl sx={styles.formControl} error={!!errors.category}>
                        <div className="popup-field">
                            <span style={{ marginBottom: '5px' }}>
                                <label htmlFor="category">Category</label>
                                <label className="required">*</label>

                            </span>

                            <Autocomplete
                                sx={{ width: '100%' }}
                                freeSolo
                                fullWidth
                                size="small"
                                value={category || ''}
                                onChange={handleCategoryChange}
                                inputValue={categoryInputValue}
                                onInputChange={(_, newInputValue) => setCategoryInputValue(newInputValue)}
                                options={categories}
                                filterOptions={(options, params) => {
                                    const filtered = filter(options, params);
                                    const { inputValue } = params;

                                    // Check if the exact input already exists in options
                                    const isExisting = options.some((option) =>
                                        typeof option === 'string' ? option.toLowerCase() === inputValue.toLowerCase() : option.title.toLowerCase() === inputValue.toLowerCase()
                                    );

                                    // If input doesn't exist, show "Add ..." option
                                    if (inputValue && !isExisting) {
                                        filtered.push({ inputValue, title: `Add "${inputValue}"`, isNew: true });
                                    }

                                    return filtered;
                                }}
                                getOptionLabel={(option) =>
                                    typeof option === 'string' ? option : option.title || option.inputValue
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        required
                                        placeholder="Select or type and add a category"
                                        sx={{
                                            width: '100%',
                                            '& .MuiInputBase-root': {
                                                padding: '6px',
                                            },
                                        }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li
                                        {...props}
                                        style={{
                                            color: option.isNew ? 'blue' : 'inherit', // Highlight new option
                                            fontStyle: option.isNew ? 'normal' : 'normal',
                                        }}
                                    >
                                        {option.title || option.inputValue || option}
                                    </li>
                                )}
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                            />


                            {errors.category && <span className="error-message">{errors.category}</span>}
                        </div>

                        <div className="popup-field">
                            <span style={{ marginBottom: '5px' }}>
                                <label htmlFor="subCategory">Subcategory</label>
                                <label className="required">*</label>
                            </span>
                            <Autocomplete
                                sx={{ width: '100%' }}
                                freeSolo
                                fullWidth
                                size="small"
                                value={subCategory || ''}
                                onChange={handleSubCategoryChange}
                                inputValue={inputValue}
                                onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                                options={subCategories}
                                filterOptions={(options, params) => {
                                    const filtered = filter(options, params);
                                    const { inputValue } = params;

                                    // Check if the exact input already exists (case-insensitive)
                                    const isExisting = options.some((option) =>
                                        typeof option === 'string' ? option.toLowerCase() === inputValue.toLowerCase() : option.title.toLowerCase() === inputValue.toLowerCase()
                                    );

                                    // Add "Add ..." only if the input doesn't exist in the options
                                    if (inputValue && !isExisting) {
                                        filtered.push({ inputValue, title: `Add "${inputValue}"`, isNew: true });
                                    }

                                    return filtered;
                                }}
                                getOptionLabel={(option) => (typeof option === 'string' ? option : option.title || option.inputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        required
                                        placeholder="Select or type and add a subcategory"
                                        size="small"
                                        sx={{
                                            width: "100%",
                                            "& .MuiOutlinedInput-root": {
                                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "none", // Keeps border color unchanged on hover
                                              },
                                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                borderColor: "none", // Keeps border color unchanged on focus
                                                boxShadow: "none",
                                              },
                                              "&.Mui-focused": {
                                                boxShadow: "none", // Removes focus shadow
                                              },
                                            },
                                          }}
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li
                                        {...props}
                                        style={{
                                            color: option.isNew ? 'blue' : 'inherit', // Highlight "Add ..." option

                                        }}
                                    >
                                        {option.title || option.inputValue || option}
                                    </li>
                                )}
                                selectOnFocus
                                clearOnBlur
                                handleHomeEndKeys
                            />

                            {errors.subCategory && <span className="error-message">{errors.subCategory}</span>}
                        </div>

                        <div className="popup-field">
                            <span style={{ marginBottom: '5px' }}>
                                <label htmlFor="description">Description</label>
                                <label className="required">*</label>
                            </span>
                            <TextareaAutosize
                                minRows={2}
                                id="description"
                                value={description}
                                required
                                onChange={handleDescriptionChange}
                                placeholder="Enter description..."
                                className={errors.description ? "error-input" : ""}
                            />
                            {errors.description && <span className="error-message">{errors.description}</span>}
                        </div>

                        <div className="popup-field">
                            <span style={{ marginBottom: '5px' }}>
                                <label htmlFor="tags-input">Add Tags</label>
                                <label className="required">*</label>
                            </span>
                            <TagsInput tags={tags} setTags={setTags} maxTags={5} className="tags-input" id="tags-input" />
                            {errors.tags && <span className="error-message">{errors.tags}</span>}
                        </div>

                        <div className="popup-field">
                            <UserSearchPicker
                                label="Share with (optional)"
                                placeholder="Search by name or email"
                                helperText="Leave empty to make this publication visible to everyone. Add users to restrict who can see it."
                                selectedUsers={allowedUsers}
                                onChange={setAllowedUsers}
                                excludeUserId={account?.localAccountId || ""}
                            />
                        </div>

                        {publishError && <div className="error-message">{publishError}</div>}

                        <div className="popup-actions">
                            <button className="popup-close-button" onClick={onClose}>
                                Close
                            </button>
                            <button
                                type="submit"
                                className="popup-publish-button"
                                onClick={handlePublishClick}
                                disabled={loader}
                            >
                                {loader ? (
                                    <>
                                        <CircularProgress size={20} color="inherit" style={{ marginRight: '8px' }} />
                                        <span>publishing...</span>
                                    </>
                                ) : (
                                    'Publish'
                                )}
                            </button>
                        </div>
                    </FormControl>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PublishPopup;